import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import GuideBreadcrumbs from '@app/components/Guide/ui/GuideBreadcrumbs'
import { RenderSwitch } from '@app/components/Guide/ui/RenderSwitch'
import ArticleCardSkeleton from '@app/components/Guide/article/ArticleCardSkeleton'

import { ERROR_403_MESSAGE } from '@app/app-constants'
import { appLink } from '@app/config/appLink'
import { useSubTabBySlug } from '@app/hooks/useStrapiQueries'
import { Tutorial } from '@app/components/Guide/types/guide.types'
import { fetchDemosBySubTabSlug, fetchTutorialsBySubTabSlug } from '@app/services/strapiServices'
import { GuidePageHeader } from '../components/GuidePageHeader'

export const SubTopicTemplatePage: React.FC = () => {
  const navigate = useNavigate()
  const { mainTopic, subTopic } = useParams()

  const {
    data: demo,
    isLoading: isLoadingDemo,
    isError: isErrorDemo,
  } = useQuery({
    queryKey: ['demo-subtopic', subTopic],
    queryFn: () => fetchDemosBySubTabSlug(),
    enabled: mainTopic === 'demos',
  })

  const {
    data: tutorials,
    isLoading: isLoadingTutorials,
    error: errorTutorials,
    isError: isErrorTutorials,
  } = useQuery<Tutorial[]>({
    queryKey: ['tutorials-subtopic', subTopic],
    queryFn: () => fetchTutorialsBySubTabSlug(subTopic as string),
    enabled: mainTopic === 'tutorials' && Boolean(subTopic),
  })

  const slug = subTopic ?? ''
  const { data: subTab, isLoading, isError, error } = useSubTabBySlug(slug)

  if (error?.message === ERROR_403_MESSAGE || errorTutorials?.message === ERROR_403_MESSAGE) {
    navigate(appLink.homepage.path)
  }

  if (isLoading || isLoadingDemo || isLoadingTutorials) {
    return (
      <div>
        <GuideBreadcrumbs links={[{ label: '' }]} />
        <Skeleton className="my-2 h-44 w-full rounded-none" />

        <div className="h-full w-full px-4 py-0 md:px-20">
          <div className="px-3 pt-3">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <ArticleCardSkeleton key={index} />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isError || !subTab || isErrorDemo || isErrorTutorials) {
    return (
      <div>
        <GuideBreadcrumbs links={[{ label: '' }]} />
        <Alert variant="destructive" className="my-2">
          <AlertDescription>
            {error instanceof Error ? error.message : 'Something went wrong.'}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const articles = (subTab.articles ?? []).map((article) => ({
    title: article.title,
    description: article.description || '',
    path: `/app/guide/${subTab.main_topic?.slug ?? ''}/${subTab.slug ?? ''}/${article.slug}`,
    image: article.hero_image?.url ?? '',
    is_external: article.is_external ?? false,
    external_link: article.external_link ?? '',
  }))

  const breadcrumbs = [
    { label: subTab?.main_topic?.title, path: `/app/guide/${mainTopic}` },
    { label: subTab?.title, path: `/app/guide/${mainTopic}/${subTab.slug}` },
  ]

  const mainTopicSlug = subTab.main_topic?.slug ?? ''

  return (
    <div className="h-full w-full px-4 py-0 md:px-20">
      <GuideBreadcrumbs links={breadcrumbs} />

      <div className="w-full max-w-6xl">
        <GuidePageHeader
          header={subTab.main_topic?.title}
          subheader={subTab.main_topic?.description || ''}
        />
      </div>

      <section>
        <h2 className="pb-4 text-left text-base font-bold uppercase leading-tight tracking-wider text-foreground">
          {subTab.title}
        </h2>

        <RenderSwitch
          subTopicSlug={mainTopicSlug}
          articles={articles}
          demos={demo}
          tutorials={tutorials}
        />
      </section>
    </div>
  )
}

export default SubTopicTemplatePage
